export interface CandleData {
  time: number; // Unix timestamp in seconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

type CandleCallback = (candle: CandleData) => void;

export class CandleWebSocket {
  private ws: WebSocket | null = null;
  private callbacks: Set<CandleCallback> = new Set();
  private currentSymbol: string | null = null;
  private currentInterval: string | null = null;
  private reconnectTimer: number | null = null;
  private isConnecting: boolean = false;
  private wsUrl: string;

  constructor(wsUrl: string = 'wss://api.hyperliquid.xyz/ws') {
    this.wsUrl = wsUrl;
  }

  subscribe(symbol: string, interval: string, callback: CandleCallback) {
    this.callbacks.add(callback);

    // If already connected to the same market/interval, just add callback
    if (
      this.currentSymbol === symbol &&
      this.currentInterval === interval &&
      this.ws?.readyState === WebSocket.OPEN
    ) {
      return;
    }

    const oldSymbol = this.currentSymbol;
    const oldInterval = this.currentInterval;
    this.currentSymbol = symbol;
    this.currentInterval = interval;

    // If WebSocket is already connected, just swap subscriptions (faster than reconnecting)
    if (this.ws?.readyState === WebSocket.OPEN && oldSymbol && oldInterval) {
      // Unsubscribe from old market/interval
      const unsubscribeMessage = {
        method: 'unsubscribe',
        subscription: {
          type: 'candle',
          coin: oldSymbol,
          interval: oldInterval,
        },
      };
      this.ws.send(JSON.stringify(unsubscribeMessage));
      console.log('Unsubscribed from candles:', oldSymbol, oldInterval);

      // Subscribe to new market/interval
      const subscribeMessage = {
        method: 'subscribe',
        subscription: {
          type: 'candle',
          coin: symbol,
          interval: interval,
        },
      };
      this.ws.send(JSON.stringify(subscribeMessage));
      console.log('Subscribed to candles:', symbol, interval);
    } else {
      // No existing connection, create new one
      this.connect();
    }
  }

  unsubscribe(callback: CandleCallback) {
    this.callbacks.delete(callback);

    // If no more callbacks, disconnect
    if (this.callbacks.size === 0) {
      this.disconnect();
    }
  }

  private connect() {
    if (this.isConnecting || !this.currentSymbol || !this.currentInterval) return;

    this.isConnecting = true;
    console.log('Connecting to candle WebSocket:', this.currentSymbol, this.currentInterval);

    try {
      this.ws = new WebSocket(this.wsUrl);

      this.ws.onopen = () => {
        console.log('Candle WebSocket connected');
        this.isConnecting = false;

        // Subscribe to candle updates
        const subscribeMessage = {
          method: 'subscribe',
          subscription: {
            type: 'candle',
            coin: this.currentSymbol!,
            interval: this.currentInterval!,
          },
        };

        this.ws?.send(JSON.stringify(subscribeMessage));
        console.log('Subscribed to candles:', subscribeMessage);
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle candle updates
          if (message.channel === 'candle' && message.data) {
            const data = message.data;

            // Transform to our format
            const candle: CandleData = {
              time: Math.floor(data.t / 1000), // Convert ms to seconds
              open: parseFloat(data.o),
              high: parseFloat(data.h),
              low: parseFloat(data.l),
              close: parseFloat(data.c),
              volume: data.v ? parseFloat(data.v) : undefined,
            };

            // Notify all callbacks
            this.callbacks.forEach(callback => callback(candle));
          }
        } catch (error) {
          console.error('Error parsing candle WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('Candle WebSocket error:', error);
        this.isConnecting = false;
      };

      this.ws.onclose = () => {
        console.log('Candle WebSocket closed');
        this.isConnecting = false;
        this.ws = null;

        // Attempt to reconnect if we still have callbacks
        if (this.callbacks.size > 0 && this.currentSymbol && this.currentInterval) {
          this.reconnectTimer = window.setTimeout(() => {
            this.connect();
          }, 3000);
        }
      };
    } catch (error) {
      console.error('Error creating candle WebSocket:', error);
      this.isConnecting = false;
    }
  }

  private disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.currentSymbol = null;
    this.currentInterval = null;
  }

  cleanup() {
    this.callbacks.clear();
    this.disconnect();
  }
}

export const candleWs = new CandleWebSocket();
